import api from '@root/src/api/api'
import { getQrCode } from '@/api/connectors/whatsapp'
import QRCode from 'qrcode'
import { PROVIDER_TYPES } from '@root/src/vars/general'

const PROVIDER_TO_BOT_PROVIDER_MAP = {
    // Провайдеры 1 и 2 у WhatsApp выведены из выбора: канал ведёт наш dh-whatsapp,
    // а бэк в getDhWaQr держит канал у стороннего, пока тот авторизован, и забирает
    // его себе в любом другом случае. Тот же приём, что уже сделан для telegramApi.
    [PROVIDER_TYPES.whatsappBasis]: 'dh-whatsapp',
    [PROVIDER_TYPES.whatsappApi]: 'dh-whatsapp',
    // Provider 1 (wApi-telegram) is deprecated: legacy telegramApi channels are
    // routed through the dh-telegram QR endpoint, which keeps them on wApi only
    // while authorized and otherwise migrates them to provider 2 under the hood.
    [PROVIDER_TYPES.telegramApi]: 'dh-telegram',
    [PROVIDER_TYPES.telegramDh]: 'dh-telegram',
    [PROVIDER_TYPES.maxBasis]: 'dh-max',
    [PROVIDER_TYPES.maxDh]: 'dh-max',
    [PROVIDER_TYPES.maxBusiness]: 'max-business',
    [PROVIDER_TYPES.whatsappDh]: 'dh-whatsapp',
}

/**
 * Рычаг поддержки: если в документе коннектора стоит forcedProvider, канал идёт к тому
 * провайдеру, которого назначили руками в админке, а не к нашему. Бэк при этом канал не
 * забирает (проверка на входе в getDhWaQr/getDhMaxQr/getDhTgQr выходит сразу).
 */
const FORCED_PROVIDER_ROUTE = {
    [PROVIDER_TYPES.whatsappBasis]: 'basis-api',
    [PROVIDER_TYPES.whatsappApi]: 'wApi-whatsapp',
    [PROVIDER_TYPES.telegramApi]: 'wApi-telegram',
    [PROVIDER_TYPES.maxBasis]: 'green-api-max',
}

const TG_QR_THROTTLE_MS = 10000
const WA_QR_THROTTLE_MS = 10000
const MAX_QR_THROTTLE_MS = 15000

export function createQrFetchTimers() {
    return {
        lastTgQrRequestTime: null,
        lastWa2QrRequestTime: null,
        lastMaxQrRequestTime: null,
        lastDataHeroesQrRequestTime: null,
    }
}

function isAuthorizedResponse(data, responseData) {
    return (
        data?.type === 'alreadyLogged' ||
        data?.status === 'authorized' ||
        data === 'authorized' ||
        data?.authorized === true ||
        responseData?.authorized === true ||
        data?.data === 'Instance already authorized' ||
        responseData?.data === 'Instance already authorized'
    )
}

async function dataUrlFromQrString(qrString) {
    const canvas = document.createElement('canvas')
    await QRCode.toCanvas(canvas, qrString)
    return canvas.toDataURL('image/png')
}

function isThrottled(lastRequestTime, throttleMs) {
    if (lastRequestTime === null || lastRequestTime === undefined) {
        return false
    }
    return Date.now() - lastRequestTime < throttleMs
}

/**
 * @returns {Promise<{
 *   status: 'authorized' | 'ready' | 'waiting' | 'password_needed' | 'unsupported' | 'error',
 *   imageSrc: string | null,
 *   throttled?: boolean
 * }>}
 */
export async function fetchChannelQrCode({ connectorId, providerType, projectId, timers, forcedProvider = null }) {
    const botProvider = getBotProviderFromProviderType(providerType, forcedProvider)
    const qrTimers = timers || createQrFetchTimers()

    if (!botProvider || !connectorId) {
        return { status: 'unsupported', imageSrc: null }
    }

    try {
        if (botProvider === 'basis-api') {
            const response = await api.networkFixer.getAppQrRework({ connectorId })
            const data = response.data ?? {}

            if (isAuthorizedResponse(data, response.data)) {
                return { status: 'authorized', imageSrc: null }
            }

            if (data?.type === 'qrCode' && data?.message) {
                return { status: 'ready', imageSrc: `data:image/jpg;base64,${data.message}` }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'wApi-whatsapp') {
            if (isThrottled(qrTimers.lastWa2QrRequestTime, WA_QR_THROTTLE_MS)) {
                return { status: 'waiting', imageSrc: null, throttled: true }
            }

            const response = await api.networkFixer.getAppQrWApiRework({ connectorId })
            qrTimers.lastWa2QrRequestTime = Date.now()

            const data = response.data ?? { type: 'waiting' }
            if (isAuthorizedResponse(data, response.data)) {
                qrTimers.lastWa2QrRequestTime = null
                return { status: 'authorized', imageSrc: null }
            }

            const qrData = data?.type === 'qrcode' ? { ...data, type: 'qrCode' } : data
            if (qrData?.type === 'qrCode' && qrData?.message) {
                return { status: 'ready', imageSrc: qrData.message }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'wApi-telegram') {
            if (isThrottled(qrTimers.lastTgQrRequestTime, TG_QR_THROTTLE_MS)) {
                return { status: 'waiting', imageSrc: null, throttled: true }
            }

            const response = await api.networkFixer.getTgWaQrRework({ connectorId })
            qrTimers.lastTgQrRequestTime = Date.now()

            const data = response.data ?? {}
            if (isAuthorizedResponse(data, response.data)) {
                qrTimers.lastTgQrRequestTime = null
                return { status: 'authorized', imageSrc: null }
            }

            const rawQr = data?.data?.qr
            if (rawQr) {
                return { status: 'ready', imageSrc: `data:image/jpg;base64,${rawQr}` }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'dh-telegram') {
            const response = await api.networkFixer.getTgDhChannelQr({ connectorId })
            const status = response.data?.status ?? null

            if (status === 'authorized') {
                return { status: 'authorized', imageSrc: null }
            }

            const rawQr = response.data?.qr ?? response.data?.data?.qr
            if (status === 'ready' && rawQr) {
                return { status: 'ready', imageSrc: `data:image/png;base64,${rawQr}` }
            }

            if (status === 'password_needed') {
                return { status: 'password_needed', imageSrc: null }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'green-api-max') {
            if (isThrottled(qrTimers.lastMaxQrRequestTime, MAX_QR_THROTTLE_MS)) {
                return { status: 'waiting', imageSrc: null, throttled: true }
            }

            const response = await api.networkFixer.getMaxQrRework({ connectorId })
            qrTimers.lastMaxQrRequestTime = Date.now()

            const data = response.data ?? {}
            if (isAuthorizedResponse(data, response.data)) {
                qrTimers.lastMaxQrRequestTime = null
                return { status: 'authorized', imageSrc: null }
            }

            if (data?.type === 'qrCode' && data?.message) {
                return { status: 'ready', imageSrc: `data:image/jpg;base64,${data.message}` }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'dh-max') {
            const response = await api.networkFixer.getDhMaxChannelQr({ connectorId })
            const status = response.data?.status ?? null

            if (status === 'authorized') {
                return { status: 'authorized', imageSrc: null }
            }

            const rawQr = response.data?.qr ?? response.data?.data?.qr
            if (status === 'ready' && rawQr) {
                return { status: 'ready', imageSrc: `data:image/png;base64,${rawQr}` }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'dh-whatsapp') {
            const response = await api.networkFixer.getWaDhChannelQr({ connectorId })
            const status = response.data?.status ?? null

            if (status === 'authorized') {
                return { status: 'authorized', imageSrc: null }
            }

            // wa-service отдаёт QR СЫРОЙ строкой Baileys (не base64 PNG) — рендерим
            // по DataHeroes-паттерну через canvas (QRCode.toCanvas → toDataURL).
            const rawQr = response.data?.qr ?? response.data?.data?.qr
            if (status === 'ready' && rawQr) {
                return { status: 'ready', imageSrc: await dataUrlFromQrString(rawQr) }
            }

            return { status: 'waiting', imageSrc: null }
        }

        if (botProvider === 'max-business') {
            const statusResponse = await api.networkFixer.getMaxBusinessStatus({ connectorId })
            const authorized =
                statusResponse?.data?.error === false && statusResponse?.data?.data === true
            return { status: authorized ? 'authorized' : 'unsupported', imageSrc: null }
        }

        if (qrTimers.lastDataHeroesQrRequestTime) {
            return { status: 'waiting', imageSrc: null, throttled: true }
        }

        if (projectId) {
            qrTimers.lastDataHeroesQrRequestTime = true
            const qrResponse = await getQrCode({ projectId, connectorId })
            if (qrResponse.error) {
                return { status: 'waiting', imageSrc: null }
            }
            if (qrResponse.data?.QR) {
                return { status: 'ready', imageSrc: await dataUrlFromQrString(qrResponse.data.QR) }
            }
        }

        return { status: 'unsupported', imageSrc: null }
    } catch (e) {
        console.error('fetchChannelQrCode error:', e)
        return { status: 'waiting', imageSrc: null }
    }
}

export function getBotProviderFromProviderType(providerType, forcedProvider) {
    if (forcedProvider && FORCED_PROVIDER_ROUTE[forcedProvider]) {
        return FORCED_PROVIDER_ROUTE[forcedProvider]
    }
    return PROVIDER_TO_BOT_PROVIDER_MAP[providerType] || ''
}
